import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Text from "@components/CustomText";
import { ScaledSheet } from '@utils/scale';
import ChevonTop from "@assets/svg/Earnings/ChevonTop";
import ChevonLeft from "@assets/svg/Earnings/ChevonLeft";


interface BreakdownItemProps {
  label: string;
  amount: string;
  is_expandable: boolean;
  extra_info: any;
  onPress: () => void;
  buttons?: any;
  key?: string;
  date?: string;
  date_range?: string;
}

/**
 * BreakdownItem Component
 * Displays individual breakdown item with expandable details
 */
const BreakdownItem: React.FC<BreakdownItemProps> = ({
  key,
  label,
  amount,
  onPress,
  is_expandable,
  extra_info,
  buttons,
  date,
  date_range
}) => {
  const navigation = useNavigation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePress = () => {
    setIsExpanded(!isExpanded);
    onPress();
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.headerContainer}
        onPress={handlePress}
        activeOpacity={0.7}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', height: 40, flex: 1}}>
          <Text style={styles.label}>{label}</Text>
          <Text style={styles.amount}>{amount}</Text>
        </View>
        {!!is_expandable && <View style={styles.arrowContainer}>
          {isExpanded ? <ChevonTop /> : <ChevonLeft />}
        </View>}
      </TouchableOpacity>

      {isExpanded && extra_info && extra_info.length > 0 && (
        <View style={styles.expandedContent}>
          {/* Earnings breakdown */}
          {extra_info.map((item: any, index: number) => (
            <View style={styles.breakdownRow} key={index}>
              <View style={{ flexDirection: 'row'}}>
                <Text style={styles.breakdownLabel}>{item.title}</Text>
                {item.is_processing && (
                  <View style={styles.processingPill}>
                    <Text style={styles.processingText}>Processing</Text>
                  </View>
                )}
              </View>
              <Text style={styles.breakdownAmount}>{item.value}</Text>
            </View>
          ))}
        </View>
      )}

      {isExpanded && buttons && buttons.length > 0 && (
        <View style={styles.buttonsContainer}>
          {buttons.map((button: any, index: number) => (
            <TouchableOpacity
              key={index}
              style={styles.button}
              onPress={() => {
                if (button.nav_to) {
                  // @ts-ignore
                  navigation.navigate(button.nav_to, { nav_params: button.nav_params || { date: date || '', date_range: date_range || '' } });
                }
              }}
              activeOpacity={0.7}
            >
              <Text style={styles.buttonText}>{button.title}</Text>
              <ChevonLeft />
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = ScaledSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  arrowContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 12,
    height: 40,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    textAlignVertical: 'center',
    lineHeight: 24,
  },
  amount: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginRight: 8,
    textAlignVertical: 'center',
    lineHeight: 24,
  },
  deductionAmount: {
    color: '#E53935',
  },
  arrow: {
    fontSize: 20,
    color: '#BDBDBD',
    fontWeight: '300',
  },
  expandedContent: {
    paddingHorizontal: 20,
    paddingBottom: 8,
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  breakdownLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#757575',
    textAlignVertical: 'center',
    lineHeight: 24,
  },
  breakdownAmount: {
    fontSize: 14,
    fontWeight: '500',
    color: '#424242',
    textAlignVertical: 'center',
    lineHeight: 24,
  },
  tripHistoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#EAEBEC',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 12,
    marginTop: 4,
  },
  tripHistoryText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  tripHistoryArrow: {
    fontSize: 20,
    color: '#BDBDBD',
    fontWeight: '300',
  },
  incentiveHistoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F5F5F5',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 12,
    marginTop: 4,
  },
  incentiveHistoryText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  incentiveHistoryArrow: {
    fontSize: 20,
    color: '#BDBDBD',
    fontWeight: '300',
  },
  buttonsContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#EAEBEC',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 12,
    marginTop: 4,
  },
  buttonTextContainer: {
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    textAlignVertical: 'center',
    lineHeight: 24,
  },
  processingPill: {
    backgroundColor: '#2195F2',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  processingText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 14,
  },
});

export default BreakdownItem;


